import * as React from 'react';
import {Plugins, Component} from '@packages/systems/wfdl';
import type {
  CollectionsType,
  ItemsType,
} from '@packages/systems/core/records/CollectionStateType';
import type {BrowserEnvironment} from '@packages/systems/core-constants';
import type {DynamoData, PaginationPageNumbers} from '../types';
import {createClientDynamoDataProvider} from './createClientDynamoDataProvider';
import PageRecord, {
  type PageRecordType,
} from '@packages/systems/core/records/PageRecord';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import {useMockPageItemData} from './useMockPageItemData';

export type DynamoDataProviderProps = {
  children: (arg1: DynamoData | null) => React.ReactElement<unknown>;
  collections: CollectionsType;
  items: ItemsType;
  itemId: string | null | undefined;
  cmsLocaleId?: string | null | undefined;
  page: PageRecordType | null | undefined;
  paginationPageNumbers: PaginationPageNumbers;
  plugins: Plugins.PluginsType;
  publishTarget: string | null | undefined;
  runtimeEnvironment: BrowserEnvironment;
  showSampleSearchResults: boolean;
  timezone: string;
  focusedSymbolNativeIdPath: NativeId[];
  rootComponentType: Qualified<Component.ComponentName>;
};

const MISSING_PAGE = PageRecord({id: 'page-record-missing'});

export const DynamoDataProvider = React.memo(
  (props: DynamoDataProviderProps) => {
    const {
      collections,
      items,
      itemId,
      page,
      paginationPageNumbers,
      plugins,
      publishTarget,
      runtimeEnvironment,
      showSampleSearchResults,
      timezone,
      cmsLocaleId,
      focusedSymbolNativeIdPath,
      rootComponentType,
    } = props;

    const mockPageItemData = useMockPageItemData(
      collections,
      items,
      itemId || null
    );

    const dynamoData = createClientDynamoDataProvider({
      collections,
      mockPageItemData,
      itemId,
      // prevent query builder crash in the rare case that the page record
      // for the current page is missing in Designer or Editor
      page: page ?? MISSING_PAGE,
      paginationPageNumbers,
      plugins,
      publishTarget,
      runtimeEnvironment,
      showSampleSearchResults,
      timezone,
      cmsLocaleId,
      focusedSymbolNativeIdPath,
      rootComponentType,
    });

    // We use a memoized inner component to wrap the props.children call so
    // that we do not call props.children if dynamoData reference has not
    // changed. This is necessary because useMockPageItemData stabilizes its
    // return value with useState which leads to extra render passes whenever
    // the return value changes.
    return (
      <InnerDynamoDataProvider dynamoData={dynamoData}>
        {props.children}
      </InnerDynamoDataProvider>
    );
  }
);

/**
 * Component whose purpose is to ensure props.children is only called when
 * props.dynamoData reference changes.
 */
const InnerDynamoDataProvider = React.memo(
  (props: {
    dynamoData: DynamoData;
    children: (arg1: DynamoData | null) => React.ReactElement<unknown>;
  }) => props.children(props.dynamoData)
);
